import {
  useState,
  useContext,
  useEffect,
  useMemo,
  ChangeEvent,
} from "react";
import { FormEvent } from "react";
import { GlobalContext } from "../../context/globalContext";
import { ButtonPrimary } from "@telefonica/mistica";
import * as S from "./styles";
import InputMask from "react-input-mask";
import SuccessMessage from "../SuccessMessage";
import handleResponsiveScroll from "../../mocks/handleResponsiveScroll";
import { api, setAuthUID } from "../../lib/api";
import {
  BR_CELLPHONE_DEFAULT_MASKED_VALUE,
  BR_CELLPHONE_INPUT_MASK,
  digitsFromBrazilMaskedPhone,
  isBrazilCellphoneMaskComplete,
} from "../../utils/brazilCellPhoneMask";
import AgilizePaperIcon from "../../assets/icons/AgilizePaperIcon";
import RemoveFileIcon from "../../assets/icons/RemoveFileIcon";

interface IFormErrors {
  fullName: string;
  contact: string;
  email: string;
}

const defaultIneligibleFields = {
  fullName: "",
  contact: BR_CELLPHONE_DEFAULT_MASKED_VALUE,
  email: "",
};

const IneligibleScreen = () => {
  const {
    resultInformations,
    setResultInformations,
    setShowIneligibleScreen,
    setMonthlyValue,
    setSelectedCity,
    setSelectedState,
  } = useContext(GlobalContext);

  const billBelow5k = useMemo(
    () => Number(resultInformations?.monthlyValue ?? NaN) < 5000,
    [resultInformations?.monthlyValue]
  );

  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [sendingFormData, setSendingFormData] = useState(false);
  const [formErrors, setFormErrors] = useState({} as IFormErrors);
  const [lightInvoiceFile, setLightInvoiceFile] = useState<File | null>(null);

  const [ineligibleFields, setIneligibleFields] = useState(
    defaultIneligibleFields
  );

  function handleGetInvoiceFile(e: ChangeEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;
    const files = target.files;

    if (files) {
      setLightInvoiceFile(files[0]);
    }
  }

  function handleChangeInputFieldValue(field: string, value: string) {
    setIneligibleFields((oldValue) => {
      return {
        ...oldValue,
        [field]: value,
      };
    });
  }

  function checkErrorsOnForm() {
    const errors = {} as IFormErrors;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!ineligibleFields.fullName) {
      errors.fullName = "Nome obrigatório";
    }

    if (!ineligibleFields.email) {
      errors.email = "E-mail obrigatório";
    } else if (!ineligibleFields.email.match(emailRegex)) {
      errors.email = "E-mail inválido";
    }

    if (!isBrazilCellphoneMaskComplete(ineligibleFields.contact)) {
      const d = digitsFromBrazilMaskedPhone(ineligibleFields.contact);
      errors.contact =
        d.length <= 2 ? "Telefone obrigatório" : "Telefone inválido";
    }

    setFormErrors(errors);
  }

  async function submitContact() {
    try {
      const formatCellphone = digitsFromBrazilMaskedPhone(
        ineligibleFields.contact
      );

      const leadData = {
        descontoVisualizado: "",
        email: ineligibleFields.email,
        leadOrigem: "Landing Page",
        mensagem: "",
        municipio: resultInformations?.city || "",
        municipioIbgeId: resultInformations?.cityId?.toString() || "",
        nome: ineligibleFields.fullName,
        origem: "Landing Page",
        telefone: formatCellphone,
        termosUso: true,
        valorAproximadoFatura: resultInformations?.monthlyValue || "",
        contato: "Telefone,E-mail",
      };

      const leadResponse = await api.post("/lead", leadData);

      if (leadResponse.data?.uid) {
        setAuthUID(leadResponse.data.uid);
      }

      if (lightInvoiceFile) {
        try {
          const formData = new FormData();
          formData.append("file", lightInvoiceFile);
          await api.post("/upload", formData);
        } catch (uploadError) {
          console.error("Erro no upload:", uploadError);
        }
      }

      if (leadResponse.data) {
        setShowSuccessScreen(true);
      } else {
        throw new Error("Erro ao criar lead");
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSendingFormData(false);
      setResultInformations(null);
    }
  }

  async function handleSubmitContact(e: FormEvent) {
    e.preventDefault();

    checkErrorsOnForm();

    setSendingFormData(true);
  }

  useEffect(() => {
    const isThereAnyErrors = Object.keys(formErrors);

    if (!isThereAnyErrors.length && sendingFormData) {
      submitContact();
    } else {
      setSendingFormData(false);
    }
  }, [formErrors]);

  function handleRedoSimulation() {
    setIneligibleFields(defaultIneligibleFields);
    setLightInvoiceFile(null);

    setShowIneligibleScreen(false);
    setResultInformations(null);
    setSelectedCity(null);
    setMonthlyValue("");
    setSelectedState("");

    setTimeout(() => {
      handleResponsiveScroll(".economySimulation", { desk: -590, mob: -540 });
    }, 300);
  }

  useEffect(() => {
    handleResponsiveScroll(".inelegibleSimulation", { desk: -500, mob: -500 });
  }, []);

  const disabledSimulationBtn = sendingFormData;

  return (
    <>
      {!showSuccessScreen ? (
        <S.IneligibleRoot>
          <S.IneligibleWrapper className="inelegibleSimulation">
            <S.InfoCol>
              <S.MainHeader>
                {import.meta.env.VITE_APP_BLACK_FRIDAY === "true"
                  ? "BLACK GUD: Condições imperdíveis por tempo limitado."
                  : "Parece que o Mercado Livre de Energia ainda não está disponível para sua empresa."}
              </S.MainHeader>

              <S.HeaderText>
                Acompanhe nossas redes sociais para ficar por dentro de todas as
                atualizações do setor.
              </S.HeaderText>

              <S.RedoSimulation $hideOnMobile={true}>
                <button onClick={() => handleRedoSimulation()} type="button">
                  Simular novamente
                </button>
              </S.RedoSimulation>
            </S.InfoCol>

            <S.IneligibleFormCol>
              <S.IneligibleFormWrapper>
                {billBelow5k ? (
                  <>
                    <h2>
                      Seu perfil de consumo parece não estar apto para o
                      Mercado Livre de Energia.
                    </h2>
                    <p>
                      Deixe seus dados e anexe uma fatura para que possamos
                      realizar uma análise e mantê-lo informado sobre as
                      novidades do setor:
                    </p>
                  </>
                ) : (
                  <>
                    <h2>
                      Neste momento nossa solução ainda não atende o seu perfil
                      de consumo.
                    </h2>
                    <br />
                    <p>
                      Deixe seus dados que entraremos em contato<br></br> quando
                      tivermos novidades:
                    </p>
                  </>
                )}
                <S.IneligibleResultForm onSubmit={handleSubmitContact}>
                  <S.InputSection>
                    <label id="campoNome">
                      <input
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("fullName", target.value)
                        }
                        type="text"
                      />
                      <S.FloatingText
                        $willFloat={Boolean(ineligibleFields.fullName)}
                        className={formErrors.fullName ? "errorMsg" : ""}
                      >
                        Nome e sobrenome
                      </S.FloatingText>
                      {formErrors.fullName && (
                        <S.FieldErrorMsg>{formErrors.fullName}</S.FieldErrorMsg>
                      )}
                    </label>
                    <label id="campoTelefoneContato">
                      <InputMask
                        value={ineligibleFields.contact}
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("contact", target.value)
                        }
                        type="text"
                        mask={BR_CELLPHONE_INPUT_MASK}
                        alwaysShowMask
                      />
                      <S.FloatingText
                        $willFloat={
                          digitsFromBrazilMaskedPhone(ineligibleFields.contact)
                            .length > 2
                        }
                        className={formErrors.contact ? "errorMsg" : ""}
                      >
                        {billBelow5k ? "Telefone" : "Telefone de contato"}
                      </S.FloatingText>
                      {formErrors.contact && (
                        <S.FieldErrorMsg>{formErrors.contact}</S.FieldErrorMsg>
                      )}
                    </label>
                  </S.InputSection>

                  <S.InputSection>
                    <label id="campoEmail">
                      <input
                        onChange={({ target }) =>
                          handleChangeInputFieldValue("email", target.value)
                        }
                        type="text"
                      />
                      <S.FloatingText
                        $willFloat={Boolean(ineligibleFields.email)}
                        className={formErrors.email ? "errorMsg" : ""}
                      >
                        {billBelow5k ? "E-mail" : "E-mail de contato"}
                      </S.FloatingText>
                      {formErrors.email && (
                        <S.FieldErrorMsg>{formErrors.email}</S.FieldErrorMsg>
                      )}
                    </label>
                  </S.InputSection>

                  {billBelow5k && (
                    <S.InvoiceAttachmentWrapper>
                      <S.InvoiceAttachmentTitle>
                        <p>
                          Anexe uma conta de luz recente (dos últimos 3 meses).
                        </p>
                      </S.InvoiceAttachmentTitle>

                      <S.InvoiceSendRow>
                        <AgilizePaperIcon />

                        <S.InvoiceFileInputWrapper>
                          <span>
                            Conta de luz:
                            <label
                              style={
                                lightInvoiceFile
                                  ? { textDecoration: "none" }
                                  : { textDecoration: "underline" }
                              }
                            >
                              {lightInvoiceFile
                                ? lightInvoiceFile.name
                                : "Selecionar arquivo"}
                              <input
                                type="file"
                                disabled={!!lightInvoiceFile}
                                onChange={(e) => handleGetInvoiceFile(e)}
                                accept=".pdf, image/*"
                                onClick={(e) => {
                                  const target = e.target as HTMLInputElement;
                                  target.value = "";
                                }}
                              />
                            </label>
                          </span>
                          {lightInvoiceFile && lightInvoiceFile.name && (
                            <button
                              onClick={() => {
                                setLightInvoiceFile(null);
                              }}
                              type="button"
                            >
                              <RemoveFileIcon />
                            </button>
                          )}
                        </S.InvoiceFileInputWrapper>
                      </S.InvoiceSendRow>
                    </S.InvoiceAttachmentWrapper>
                  )}

                  <S.SendButton $formLoading={sendingFormData}>
                    <ButtonPrimary
                      className="firstForm_conversion_desqualificado"
                      loadingText="Enviando..."
                      submit
                      disabled={
                        disabledSimulationBtn ||
                        !ineligibleFields.fullName.trim() ||
                        !ineligibleFields.email.trim() ||
                        !isBrazilCellphoneMaskComplete(
                          ineligibleFields.contact
                        )
                      }
                      showSpinner={sendingFormData}
                    >
                      Enviar
                    </ButtonPrimary>
                  </S.SendButton>
                </S.IneligibleResultForm>

                <S.FormFooter>
                  <p>
                    Ao enviar você autoriza a GUD e seus parceiros a entrar em
                    contato com você via e-mail, telefone ou WhatsApp e declara
                    ter lido a{" "}
                    <a target="__blank" href="/normas-de-privacidade">
                      Termo de Privacidade
                    </a>
                    .
                  </p>
                </S.FormFooter>
              </S.IneligibleFormWrapper>

              <S.RedoSimulation $hideOnMobile={false}>
                <button onClick={() => handleRedoSimulation()} type="button">
                  Simular novamente
                </button>
              </S.RedoSimulation>
            </S.IneligibleFormCol>
          </S.IneligibleWrapper>
        </S.IneligibleRoot>
      ) : (
        <SuccessMessage ineligible={true} />
      )}
    </>
  );
};

export default IneligibleScreen;
